import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { AppInput, FormButtonGroup } from 'features/components';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { User } from 'api/bindings/User';
import { PutUserRequest } from 'api/bindings/PutUserRequest';
import { usePutUserMutation } from 'api';
import { UserResponse } from 'api/bindings/UserResponse';

export interface EditUserDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  user: UserResponse;
  onSuccess?: () => void;
  onClose: () => void;
}

interface Form extends PutUserRequest {}

export const EditUserDrawer = (props: EditUserDrawerProps) => {
  const { user, onSuccess, onClose, ...rest } = props;

  const toast = useToast();
  const [putUser] = usePutUserMutation();

  // State
  const form = useForm<Form>({
    defaultValues: user,
    // defaultValues: {
    //   firstName: user.firstName || null,
    //   lastName: user.lastName || null,
    //   preferredName: user.preferredName || null,
    //   purchaseOrderApprovalAuthority:
    //     user.purchaseOrderApprovalAuthority?.toString() ?? null,
    //   workOrderApprovalAuthority:
    //     user.workOrderApprovalAuthority?.toString() ?? null,
    //   invoiceApprovalAuthority:
    //     user.invoiceApprovalAuthority?.toString() ?? null,
    //   afeApprovalAuthority: user.afeApprovalAuthority?.toString() ?? null,
    //   roles: null, // TODO: Initialize from user roles if needed
    //   claims: null, // TODO: Initialize from user claims if needed
    // },
    mode: 'all',
  });
  const { register, watch, handleSubmit, formState, reset } = form;

  const [firstName, preferredName, lastName] = watch([
    'firstName',
    'preferredName',
    'lastName',
  ]);
  const formName = useMemo(
    () => `Edit User: ${preferredName || firstName} ${lastName}`,
    [firstName, lastName, preferredName]
  );

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await putUser({ id: user.id, ...form });
        if ('error' in result) {
          toast({
            title: `Failed to save user ${
              form.preferredName || form.firstName
            } ${form.lastName}`,
            description: (result.error as any)?.data?.message,
            status: 'error',
          });
          return;
        }
        toast({
          title: `Saved user ${form.preferredName || form.firstName} ${
            form.lastName
          } successfully!`,
          status: 'success',
        });
        reset();
        onClose();
      } catch {}
    },
    [onClose, putUser, reset, toast, user.id]
  );
  // const onSubmit = async (data: Form) => {
  //   try {
  //     // Only send fields that are not null/empty
  //     const updateData: PutUserRequest = {
  //       firstName: data.firstName || null,
  //       lastName: data.lastName || null,
  //       preferredName: data.preferredName || null,
  //       purchaseOrderApprovalAuthority: data.purchaseOrderApprovalAuthority,
  //       workOrderApprovalAuthority: data.workOrderApprovalAuthority,
  //       invoiceApprovalAuthority: data.invoiceApprovalAuthority,
  //       afeApprovalAuthority: data.afeApprovalAuthority,
  //       roles: data.roles,
  //       claims: data.claims,
  //     };

  //     await updateUser({
  //       id: Number(user.id),
  //       data: updateData,
  //     }).unwrap();
  //   } catch (err) {
  //     toast({
  //       title: 'Error Updating User',
  //       description: 'Failed to update user. Please try again.',
  //       status: 'error',
  //       duration: 5000,
  //       isClosable: true,
  //     });
  //   }
  // };

  // useEffect(() => {
  //   if (isSuccess) {
  //     toast({
  //       title: 'User Updated',
  //       description: 'User has been successfully updated',
  //       status: 'success',
  //       duration: 3000,
  //       isClosable: true,
  //     });
  //     onSuccess?.();
  //     onClose();
  //   }
  // }, [isSuccess, onSuccess, onClose, toast]);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Drawer onClose={onClose} {...rest} size="xl">
          <DrawerOverlay />
          <DrawerCloseButton />
          <DrawerContent>
            <DrawerHeader>{formName}</DrawerHeader>
            <DrawerBody>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="first">First Name:</FormLabel>
                  <AppInput
                    id="first"
                    {...register('firstName')}
                    isInvalid={!!formState.errors.firstName}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="preferred">Preferred Name:</FormLabel>
                  <AppInput id="preferred" {...register('preferredName')} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="last">Last Name:</FormLabel>
                  <AppInput
                    id="last"
                    {...register('lastName')}
                    isInvalid={!!formState.errors.lastName}
                  />
                </FormControl>
                <FormControl isDisabled>
                  <FormLabel htmlFor="email">Email:</FormLabel>
                  <AppInput
                    id="email"
                    value={user.email}
                    isDisabled
                    placeholder="Email cannot be changed"
                  />
                </FormControl>
              </Stack>
              <Stack spacing={4} mt={4}>
                <FormControl>
                  <FormLabel htmlFor="defaultAfe">AFE Authority:</FormLabel>
                  <AppInput
                    id="defaultAfe"
                    type="currency"
                    {...register('afeApprovalAuthority')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="poAuthority">
                    Purchase Order Authority:
                  </FormLabel>
                  <AppInput
                    id="poAuthority"
                    type="currency"
                    {...register('purchaseOrderApprovalAuthority')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="woAuthority">
                    Work Order Authority:
                  </FormLabel>
                  <AppInput
                    id="woAuthority"
                    type="currency"
                    {...register('workOrderApprovalAuthority')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="invoiceAuthority">
                    Invoice Authority:
                  </FormLabel>
                  <AppInput
                    id="invoiceAuthority"
                    type="currency"
                    {...register('invoiceApprovalAuthority')}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onCancel={onClose}
                onConfirm={handleSubmit(onSubmit)}
              />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </FormProvider>
  );
};
